import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Lock, User, Loader2, AlertCircle, LogOut, Phone, Mail, UserCircle } from 'lucide-react';
import { SplaLogo, SplaIcon } from '../components/SplaLogo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { authUser, logLogin, fetchOnePagerSWR, sendInvestorRequest } from '../lib/sheets';
import OnePager from '../components/OnePager';

// Request Access Modal Component
const RequestAccessModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({ name: '', phone: '', email: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    await sendInvestorRequest(formData);
    setIsSubmitting(false);
    setSubmitted(true);
    setTimeout(() => {
      onClose();
      setSubmitted(false);
      setFormData({ name: '', phone: '', email: '' });
    }, 2000);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-[#0a0a0a] border-white/10 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="font-benzin text-xl">Запросить доступ</DialogTitle>
        </DialogHeader>
        
        {submitted ? (
          <div className="py-8 text-center">
            <p className="text-white/70">Спасибо! Мы свяжемся с вами в ближайшее время.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="req-name" className="text-white/50 text-sm">Имя</Label>
              <div className="relative">
                <UserCircle className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                <Input
                  id="req-name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Ваше имя"
                  data-testid="request-name-input"
                  className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/30
                            focus:border-white/30 focus:ring-white/20"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="req-phone" className="text-white/50 text-sm">Телефон</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                <Input
                  id="req-phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  placeholder="+7 (999) 123-45-67"
                  data-testid="request-phone-input"
                  className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/30
                            focus:border-white/30 focus:ring-white/20"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="req-email" className="text-white/50 text-sm">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                <Input
                  id="req-email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  placeholder="email@example.com"
                  data-testid="request-email-input"
                  className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/30
                            focus:border-white/30 focus:ring-white/20"
                  required
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={isSubmitting}
              data-testid="request-submit-btn"
              className="w-full bg-white/10 hover:bg-white/20 text-white font-medium py-5 mt-4
                        border border-white/20 transition-all duration-300"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Отправка...
                </>
              ) : (
                'Отправить заявку'
              )}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

const Investor = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [investorName, setInvestorName] = useState('');
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [onePagerContent, setOnePagerContent] = useState('');

  // Check if already authenticated from session — re-verify against the sheet
  useEffect(() => {
    const storedAuth = sessionStorage.getItem('splav_investor_auth');
    const storedName = sessionStorage.getItem('splav_investor_name');
    const storedUser = sessionStorage.getItem('splav_investor_user');
    const storedPass = sessionStorage.getItem('splav_investor_pass');

    if (storedAuth !== 'true' || !storedUser || !storedPass) return;

    // Оптимистично показываем кабинет, но в фоне перепроверяем
    setIsAuthenticated(true);
    if (storedName) setInvestorName(storedName);

    authUser('Investor accounts', storedUser, storedPass).then((result) => {
      if (!result.ok) {
        // Учётка удалена или пароль изменён — выкидываем
        setIsAuthenticated(false);
        setInvestorName('');
        sessionStorage.removeItem('splav_investor_auth');
        sessionStorage.removeItem('splav_investor_name');
        sessionStorage.removeItem('splav_investor_user');
        sessionStorage.removeItem('splav_investor_pass');
      } else if (result.name && result.name !== storedName) {
        // Имя в таблице обновилось
        setInvestorName(result.name);
        sessionStorage.setItem('splav_investor_name', result.name);
      }
    }).catch(() => {});
  }, []);

  // Подтягиваем ONE-PAGER!A1 после авторизации
  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    const swr = fetchOnePagerSWR();
    if (swr.cached) setOnePagerContent(swr.cached);
    swr.fresh.then((v) => {
      if (!cancelled && v && v.trim()) setOnePagerContent(v);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [isAuthenticated]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const result = await authUser('Investor accounts', username, password);

      if (result.ok) {
        const foundName = result.name || 'Инвестор';
        setIsAuthenticated(true);
        setInvestorName(foundName);
        sessionStorage.setItem('splav_investor_auth', 'true');
        sessionStorage.setItem('splav_investor_name', foundName);
        sessionStorage.setItem('splav_investor_user', username);
        sessionStorage.setItem('splav_investor_pass', password);

        // Лог входа — не блокирует UI
        const device = navigator.userAgent.replace(/^.*?(Chrome|Safari|Firefox|Edge|Opera)[\/\s](\S+).*$/, '$1/$2')
          || navigator.userAgent.substring(0, 80);
        fetch('https://api.ipify.org?format=json')
          .then(r => r.json())
          .then(d => logLogin({ username, ip: d.ip, device }))
          .catch(() => logLogin({ username, ip: 'N/A', device }));
      } else {
        setError('Неверное имя пользователя или пароль');
      }
    } catch (err) {
      console.error('Auth error:', err);
      setError('Ошибка при проверке данных. Попробуйте позже.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setInvestorName('');
    setUsername('');
    setPassword('');
    sessionStorage.removeItem('splav_investor_auth');
    sessionStorage.removeItem('splav_investor_name');
    sessionStorage.removeItem('splav_investor_user');
    sessionStorage.removeItem('splav_investor_pass');
  };

  // Authenticated view - Dashboard
  if (isAuthenticated) {
    return (
      <div 
        data-testid="investor-dashboard"
        className="min-h-screen bg-black"
      >
        {/* Header */}
        <header className="sticky top-0 z-50 backdrop-blur-xl bg-black/80 border-b border-white/10">
          <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link 
              to="/"
              data-testid="back-to-home-btn"
              className="inline-flex items-center gap-2 text-white/50 hover:text-white transition-colors duration-300"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm">Назад</span>
            </Link>
            <Link to="/">
              <SplaIcon size={28} className="text-white" />
            </Link>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              data-testid="logout-btn"
              className="text-white/50 hover:text-white hover:bg-white/10 transition-all duration-300"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Выйти
            </Button>
          </div>
        </header>

        {/* Dashboard content */}
        <main className="max-w-5xl mx-auto px-4 lg:px-6 py-8 lg:py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
            className="mb-8 lg:mb-10 text-center"
          >
            <h1 className="font-benzin text-2xl lg:text-3xl text-white mb-3">
              Добро пожаловать, {investorName}
            </h1>
            <p className="text-white/50 text-sm lg:text-base leading-relaxed">
              Информация о продукте, бизнес-модели и команде Splav.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1, ease: [0.25, 0.1, 0.25, 1] }}
          >
            <OnePager content={onePagerContent} />
          </motion.div>
        </main>
      </div>
    );
  }

  // Login form view
  return (
    <div 
      data-testid="investor-auth-page"
      className="min-h-screen bg-black flex flex-col"
    >
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-black/80 border-b border-white/10">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link 
            to="/"
            data-testid="back-to-home-btn-mobile"
            className="inline-flex items-center gap-2 text-white/50 hover:text-white transition-colors duration-300"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Назад</span>
          </Link>
          <Link to="/">
            <SplaIcon size={28} className="text-white" />
          </Link>
        </div>
      </header>

      {/* Form container */}
      <div className="flex-1 flex items-start justify-center p-6 pt-10">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
          className="w-full max-w-lg"
        >
          <div className="text-center mb-6">
            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.4 }}
              className="inline-flex items-center justify-center w-12 h-12 bg-white/5 border border-white/10 rounded-xl mb-4"
            >
              <Lock className="w-5 h-5 text-white/70" />
            </motion.div>
            <h1 className="font-benzin text-2xl text-white mb-4">
              Вход для инвесторов
            </h1>
            
            <p className="text-white/50 text-[15px] leading-relaxed">
              В целях защиты стратегических данных и финансовых метрик, детальная информация о продукте предоставляется только авторизованным партнерам.
            </p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4 mt-6">
            <div className="space-y-2">
              <Label htmlFor="username" className="text-white/50 text-sm">
                Имя пользователя
              </Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                <Input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Введите имя пользователя"
                  data-testid="username-input"
                  className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/30
                            focus:border-white/30 focus:ring-white/20 transition-all duration-300"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-white/50 text-sm">
                Пароль
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Введите пароль"
                  data-testid="password-input"
                  className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/30
                            focus:border-white/30 focus:ring-white/20 transition-all duration-300"
                  required
                />
              </div>
            </div>

            <AnimatePresence>
              {error && (
                <motion.div 
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  data-testid="error-message"
                  className="flex items-center gap-2 p-3 bg-white/5 border border-white/10 rounded-lg text-white/70 text-sm"
                >
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {error}
                </motion.div>
              )}
            </AnimatePresence>

            <Button
              type="submit"
              disabled={isLoading}
              data-testid="login-submit-btn"
              className="w-full bg-white text-black hover:bg-white/90 font-medium py-5 
                        transition-all duration-300 active:scale-[0.98]"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Проверка...
                </>
              ) : (
                'Войти'
              )}
            </Button>

            {/* Request Access Button */}
            <Button
              type="button"
              variant="ghost"
              onClick={() => setShowRequestModal(true)}
              data-testid="request-access-btn"
              className="w-full text-white/50 hover:text-white hover:bg-white/5 font-medium py-5 
                        border border-white/10 transition-all duration-300"
            >
              Запросить доступ
            </Button>
          </form>
        </motion.div>
      </div>

      {/* Request Access Modal */}
      <RequestAccessModal 
        isOpen={showRequestModal} 
        onClose={() => setShowRequestModal(false)} 
      />
    </div>
  );
};

export default Investor;
